import { useMemo, useState } from 'react';
import { useParams } from 'quartz-routing/react-router';
import { useTicket } from '../data/useTicket';
import { useConfiguredTicketProperties } from '../data/usePortalData';
import { TicketInfoGrid } from './TicketInfo/TicketInfoGrid';
import { sortConfiguredTicketProperties } from '../utils/sortConfiguredTicketProperties';
import { SidebarCollapseIcon } from 'foundations-assets/icons/components/SidebarCollapseIcon';
import { SidebarExpandIcon } from 'foundations-assets/icons/components/SidebarExpandIcon';
import styled from 'foundations-theming-specialty/utils';
import I18n from 'I18n';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { CloseTicket } from './CloseTicket';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CollapsiblePanelHeader, IconButtonWrapper } from './CollapsiblePanelHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PanelWrapper = styled.div.withConfig({
  displayName: "TicketInfoPanel__PanelWrapper"
})(["background-color:", ";display:flex;flex-direction:column;height:100%;flex:", ";width:", ";border-inline-end-width:1px;border-inline-end-style:solid;border-color:", ";padding-inline:", ";padding-block:", ";transition:width 0.1s ease,flex 0.1s ease;&:hover ", ",&:focus-within ", "{width:", ";opacity:1;transform:translateX(0);margin-inline-end:", ";pointer-events:auto;}"], ({
  theme
}) => theme.color['fill-surface-default'], ({
  $isCollapsed
}) => $isCollapsed ? '0 0 80px' : '0 0 350px', ({
  $isCollapsed
}) => $isCollapsed ? '80px' : '350px', ({
  theme
}) => theme.color['border-core-disabled'], ({
  theme,
  $isCollapsed
}) => $isCollapsed ? theme.space['600'] : theme.space['800'], ({
  theme
}) => theme.space['400'], IconButtonWrapper, IconButtonWrapper, ({
  theme
}) => theme.space['800'], ({
  theme,
  $isCollapsed
}) => $isCollapsed ? '0' : theme.space['200']);
const ContentWrapper = styled.div.withConfig({
  displayName: "TicketInfoPanel__ContentWrapper"
})(["display:flex;flex-direction:column;gap:", ";flex:1;min-width:0;opacity:", ";transition:opacity 0.3s ease;overflow-x:hidden;overflow-y:auto;"], ({
  theme
}) => theme.space['500'], ({
  $isCollapsed
}) => $isCollapsed ? '0' : '1');
export function TicketInfoPanel({
  isMobile,
  isInfoPanelOpen,
  setIsInfoPanelOpen
}) {
  const {
    ticketId
  } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const configuredTicketProperties = useConfiguredTicketProperties();
  const ticketData = useTicket(Number(ticketId));
  const configuredTicketPropertiesToDisplay = useMemo(() => {
    if (!configuredTicketProperties) return [];
    return sortConfiguredTicketProperties(configuredTicketProperties).filter(({
      propertyName
    }) => propertyName !== 'subject' && propertyName !== 'content');
  }, [configuredTicketProperties]);

  // Mobile: render as overlay Panel
  if (isMobile) {
    if (!isInfoPanelOpen) return null;
    return /*#__PURE__*/_jsxs(Panel, {
      align: "left",
      inlineSize: "100dvw",
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        color: "icon-core-default",
        onClick: () => setIsInfoPanelOpen(false),
        "aria-label": I18n.text('customer-portal-app.ticketInfoPanel.ariaClose')
      }), /*#__PURE__*/_jsxs(PanelSection, {
        display: "flex",
        flex: "1",
        flexDirection: "column",
        paddingBlock: "600",
        gap: "400",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h2",
          color: "text-core-default",
          typography: "heading-300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.ticketInfoPanel.details"
          })
        }), /*#__PURE__*/_jsx(TicketInfoGrid, {
          ticketId: ticketId,
          configuredTicketProperties: configuredTicketPropertiesToDisplay,
          loading: ticketData.loading,
          error: !!ticketData.error,
          refetchTicket: ticketData.refetch
        }), /*#__PURE__*/_jsx(CloseTicket, {
          ticket: ticketData.data,
          ticketId: Number(ticketId)
        })]
      })]
    });
  }

  // Desktop: render with collapsible sidebar
  return /*#__PURE__*/_jsxs(PanelWrapper, {
    $isCollapsed: isCollapsed,
    children: [/*#__PURE__*/_jsx(CollapsiblePanelHeader, {
      isCollapsed: isCollapsed,
      onToggle: () => setIsCollapsed(!isCollapsed),
      tooltip: isCollapsed ? I18n.text('customer-portal-app.panel.expandTooltip') : I18n.text('customer-portal-app.panel.collapseTooltip'),
      ariaLabel: isCollapsed ? I18n.text('customer-portal-app.ticketInfoPanel.expand') : I18n.text('customer-portal-app.ticketInfoPanel.collapse'),
      icon: isCollapsed ? /*#__PURE__*/_jsx(SidebarExpandIcon, {
        color: "icon-core-default",
        size: "800"
      }) : /*#__PURE__*/_jsx(SidebarCollapseIcon, {
        color: "icon-core-default",
        size: "800"
      }),
      title: /*#__PURE__*/_jsx(Text, {
        as: "h2",
        color: "text-core-default",
        typography: "heading-300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.ticketInfoPanel.details"
        })
      })
    }), /*#__PURE__*/_jsx(ContentWrapper, {
      $isCollapsed: isCollapsed,
      children: !isCollapsed && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(TicketInfoGrid, {
          ticketId: ticketId,
          configuredTicketProperties: configuredTicketPropertiesToDisplay,
          loading: ticketData.loading,
          error: !!ticketData.error,
          refetchTicket: ticketData.refetch
        }), /*#__PURE__*/_jsx(CloseTicket, {
          ticket: ticketData.data,
          ticketId: Number(ticketId)
        })]
      })
    })]
  });
}